import React, { useState } from 'react';
import {
  Plus,
  Search,
  FileText,
  Clock,
  CheckCircle,
  ChevronRight,
  ArrowLeft,
  Trash2,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { AppLayout } from '../components/layout/AppLayout';

import PlanLimitModal from '../components/ui/PlanLimitModal';
import Pricing from './Pricing';
import SetupForm from './SetupForm';
import { useUser, useAuth } from '../auth/ClerkWrapper';
import api from '../utils/api';

const Workspaces = ({
  assignments = [],
  onCreateNew, // Now passed as a prop to handle the actual creation logic
  onSelect,
  onDelete,
  onLogout,
  activeView = 'WORKSPACES',
  onViewChange,
  userUsage = null, // User usage data for plan limit checking
  isLoading = false,
  onRefresh = null
}) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [mode, setMode] = useState('LIST'); // 'LIST', 'NEW_ASSIGNMENT', 'PLAN_MANAGEMENT'

  // Plan limit modal state
  const [planLimitModal, setPlanLimitModal] = useState({
    isOpen: false,
    limitInfo: null
  });

  // Filter assignments based on search
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAssignments = assignments
    .filter(a => a.config?.title?.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => (b.isDemo ? 1 : 0) - (a.isDemo ? 1 : 0));

  // Check if user can create a new assignment based on their plan limits
  const canCreateAssignment = () => {
    if (!userUsage) {
      // If usage data is not loaded yet, allow (will be caught by backend)
      return { allowed: true };
    }

    const packageType = typeof userUsage.package === 'string' 
      ? userUsage.package 
      : userUsage.package?.type || 'luna';
    
    const workspaceUsage = userUsage.usage?.workspaces;
    const currentCount = workspaceUsage?.used || 0;
    const limit = workspaceUsage?.limit || 1;

    // Check for unlimited (-1 means unlimited)
    if (limit === -1) {
      return { allowed: true };
    }

    // Check if limit reached
    if (currentCount >= limit) {
      return {
        allowed: false,
        message: `You've reached your ${packageType} plan limit of ${limit} workspace${limit !== 1 ? 's' : ''}`,
        limit: limit,
        current: currentCount,
        package: packageType,
        upgradeRequired: true
      };
    }

    return { allowed: true };
  };

  // Handle clicking "New Assignment" button - check limits first
  const handleNewAssignmentClick = () => {
    const limitCheck = canCreateAssignment();
    if (!limitCheck.allowed) {
      // Show plan limit modal immediately
      setPlanLimitModal({
        isOpen: true,
        limitInfo: limitCheck
      });
    } else {
      // Allow user to proceed to form
      setMode('NEW_ASSIGNMENT');
    }
  };

  const handleCreateAssignmentComplete = async (config) => {
    try {
      await onCreateNew(config);
      // Don't set mode to LIST here - the parent component (GradeMindApp) will handle
      // navigation after creation completes
    } catch (error) {
      console.error('❌ Error creating assignment:', error);
      
      // Check if it's a plan limit error
      if (error.response?.data?.upgradeRequired) {
        setPlanLimitModal({
          isOpen: true,
          limitInfo: error.response.data
        });
      } else {
        // Re-throw other errors to be handled by the caller
        throw error;
      }
    }
  };

  // Render the Header based on mode
  const renderHeader = () => {
    if (mode === 'NEW_ASSIGNMENT') {
      return (
        <div className="mb-8 animate-in fade-in slide-in-from-top-2 duration-300">
          <button
            onClick={() => setMode('LIST')}
            className="flex items-center text-sm text-zinc-500 hover:text-black transition-colors mb-4 group"
          >
            <ArrowLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
            Back to Workspaces
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-black rounded-xl">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-black">New Assignment</h1>
              <p className="text-zinc-500">Create a new grading workflow</p>
            </div>
          </div>
        </div>
      );
    }

    // Default List Header
    return (
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-black">Workspaces</h1>
          <p className="text-zinc-500">Manage your assignments</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-black w-64 transition-all hover:border-zinc-300"
            />
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              title="Refresh assignments"
              className="p-2 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <Button
            onClick={handleNewAssignmentClick}
            icon={Plus}
            variant="outline"
            className="bg-white border-zinc-200 hover:bg-black hover:text-white hover:border-black"
          >
            New Assignment
          </Button>
        </div>
      </div>
    );
  };

  // Render Content
  if (mode === 'PLAN_MANAGEMENT') {
    return (
      <Pricing
        onSelectPlan={async (plan, billingInterval = 'monthly') => {
          // Handle paid plans (Mercury, Neptune, Jupiter)
          const paidPlans = ['Mercury', 'Neptune', 'Jupiter'];
          if (paidPlans.includes(plan)) {
            try {
              const token = await getToken();
              const response = await api.post('/payments/create-checkout-session', {
                packageType: plan.toLowerCase(),
                billingInterval: billingInterval
              }, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });

              if (response.data.url) {
                window.location.href = response.data.url;
              }
            } catch (err) {
              console.error('Payment error:', err);
              alert(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
            }
          } else if (plan === 'Institution') {
            window.location.href = 'mailto:contact@grademind.ai?subject=Institution Plan Inquiry';
          } else if (plan === 'Luna') {
            // Luna (free) - just go back
            setMode('LIST');
          } else {
            // Unknown plan - just go back
            setMode('LIST');
          }
        }}
        onBack={() => setMode('LIST')}
      />
    );
  }

  if (mode === 'NEW_ASSIGNMENT') {
    return (
      <AppLayout activeView={activeView} onViewChange={onViewChange} onLogout={onLogout}>
        <div className="max-w-5xl mx-auto">
          {renderHeader()}
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden animate-in fade-in duration-300 h-[650px]">
            <SetupForm
              onComplete={handleCreateAssignmentComplete}
              onCancel={() => setMode('LIST')}
            />
          </div>
        </div>
      </AppLayout>
    );
  }

  // List View
  return (
    <AppLayout activeView={activeView} onViewChange={onViewChange} onLogout={onLogout}>
      <div className="space-y-6">
        {renderHeader()}

        {/* Assignments List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {isLoading ? (
            // Loading skeleton cards
            [1, 2, 3].map(i => (
              <div key={i} className="bg-white p-5 rounded-xl border border-zinc-200 animate-pulse">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-10 h-10 bg-zinc-100 rounded-xl" />
                  <div className="w-20 h-5 bg-zinc-100 rounded-full" />
                </div>
                <div className="h-4 bg-zinc-100 rounded w-3/4 mb-2" />
                <div className="h-3 bg-zinc-100 rounded w-full mb-1" />
                <div className="h-3 bg-zinc-100 rounded w-2/3 mb-4" />
                <div className="pt-4 border-t border-zinc-100 flex justify-between">
                  <div className="h-3 bg-zinc-100 rounded w-24" />
                  <div className="h-3 bg-zinc-100 rounded w-12" />
                </div>
              </div>
            ))
          ) : filteredAssignments.length === 0 ? (
            <div className="col-span-full py-16 text-center bg-white rounded-xl border-2 border-dashed border-zinc-200">
              <div className="w-14 h-14 bg-zinc-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-zinc-400" />
              </div>
              <h3 className="text-black font-semibold mb-1">No assignments yet</h3>
              <p className="text-zinc-500 text-sm mb-5">Create your first assignment to get started</p>
              <Button size="sm" onClick={handleNewAssignmentClick} icon={Plus}>
                Create Assignment
              </Button>
            </div>
          ) : (
            filteredAssignments.map((assignment) => (
              <div
                key={assignment.id}
                onClick={() => onSelect(assignment.id)}
                className="group bg-white p-5 rounded-xl border border-zinc-200 hover:border-black/20 hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2.5 bg-zinc-100 rounded-xl group-hover:bg-black group-hover:text-white transition-all duration-300">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex items-center gap-2">
                    {assignment.isDemo && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
                        Demo
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-[10px] font-medium text-zinc-400 bg-zinc-50 px-2 py-1 rounded-full border border-zinc-100">
                      <Calendar className="w-3 h-3" />
                      {(() => {
                        if (!assignment.createdAt) return 'N/A';
                        const date = new Date(assignment.createdAt);
                        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                      })()}
                    </span>
                    {assignment.processingStatus === 'processing' && (
                      <span className="flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 px-2 py-1 rounded-full">
                        <Clock className="w-3 h-3 animate-spin" />
                        Processing
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(assignment.id);
                      }}
                      className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete Assignment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="font-semibold text-black mb-1 group-hover:text-black transition-colors truncate">
                  {assignment.config.title}
                </h3>
                <p className="text-sm text-zinc-500 line-clamp-2 mb-4 h-10">
                  {assignment.config.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      {assignment.submissionCount || 0} submissions
                    </span>
                  </div>
                  <span className="text-xs font-medium text-zinc-400 group-hover:text-black transition-colors flex items-center gap-1">
                    Open <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Plan Limit Modal */}
      <PlanLimitModal
        isOpen={planLimitModal.isOpen}
        limitInfo={planLimitModal.limitInfo}
        onClose={() => setPlanLimitModal({ isOpen: false, limitInfo: null })}
        onUpgrade={() => setMode('PLAN_MANAGEMENT')}
      />
    </AppLayout>
  );
};

export default Workspaces;
